/**
 * EngagementMetrics - Display user engagement metrics
 * 
 * Features:
 * - Display average session duration in "25m 30s" format
 * - Display average sessions per user as decimal (e.g., "3.5 sessions/user")
 * - Display total time spent in app (total hours and average per user)
 * - Date range filter (7d, 30d, 90d, custom)
 * - Subscription tier filter (All, Free, Premium, Family)
 * - Update metrics within 3 seconds when filters change
 * - Responsive design for mobile, tablet, and desktop
 * 
 * Requirements: 3.1, 3.3, 3.4, 3.17, 3.18, 3.19, 3.20
 */

import React, { useState } from 'react';
import { useEngagementMetrics } from '../../hooks/useEngagementMetrics';
import './EngagementMetrics.css';

const EngagementMetrics = ({ className = '' }) => {
  const [period, setPeriod] = useState('30d');
  const [tier, setTier] = useState('all');

  const { data, loading, error, refresh } = useEngagementMetrics(period, tier);

  // Format seconds to "25m 30s" format
  const formatDuration = (seconds) => {
    if (!seconds || seconds === 0) return '0s';
    
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    
    const parts = [];
    if (hours > 0) parts.push(`${hours}h`);
    if (minutes > 0) parts.push(`${minutes}m`);
    if (secs > 0 || parts.length === 0) parts.push(`${secs}s`);
    
    return parts.join(' ');
  };

  // Format hours to readable format
  const formatHours = (seconds) => {
    if (!seconds || seconds === 0) return '0h';
    
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    
    if (hours === 0) return `${minutes}m`;
    if (minutes === 0) return `${hours}h`;
    return `${hours}h ${minutes}m`;
  };

  // Handle period change
  const handlePeriodChange = (newPeriod) => {
    setPeriod(newPeriod);
  };

  // Handle tier change
  const handleTierChange = (e) => {
    setTier(e.target.value);
  };

  // Loading state
  if (loading && !data) {
    return (
      <div className={`engagement-metrics ${className}`}>
        <div className="engagement-metrics__header">
          <h2 className="engagement-metrics__title">User Engagement</h2>
          <div className="engagement-metrics__filters engagement-metrics__filters--loading">
            <div className="engagement-metrics__skeleton-filter"></div>
            <div className="engagement-metrics__skeleton-filter"></div>
          </div>
        </div>
        <div className="engagement-metrics__grid">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="engagement-metrics__card engagement-metrics__card--skeleton">
              <div className="engagement-metrics__skeleton-label"></div>
              <div className="engagement-metrics__skeleton-value"></div>
              <div className="engagement-metrics__skeleton-subtitle"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className={`engagement-metrics ${className}`}>
        <div className="engagement-metrics__header">
          <h2 className="engagement-metrics__title">User Engagement</h2>
        </div>
        <div className="engagement-metrics__error">
          <svg className="engagement-metrics__error-icon" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="8" x2="12" y2="12"></line>
            <line x1="12" y1="16" x2="12.01" y2="16"></line>
          </svg>
          <p className="engagement-metrics__error-message">{error}</p>
          <button className="engagement-metrics__retry-button" onClick={refresh}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Empty state
  if (!data || !data.data) {
    return (
      <div className={`engagement-metrics ${className}`}>
        <div className="engagement-metrics__header">
          <h2 className="engagement-metrics__title">User Engagement</h2>
        </div>
        <div className="engagement-metrics__empty">
          <p>No engagement data available</p>
        </div>
      </div>
    );
  }

  const metrics = data.data;

  return (
    <div className={`engagement-metrics ${className}`} role="region" aria-label="User engagement metrics">
      {/* Header with Filters */}
      <div className="engagement-metrics__header">
        <h2 className="engagement-metrics__title">User Engagement</h2>
        
        <div className="engagement-metrics__filters">
          {/* Period Filter */}
          <div className="engagement-metrics__filter-group">
            <label className="engagement-metrics__filter-label" htmlFor="period-filter">
              Time Period
            </label>
            <div className="engagement-metrics__period-buttons" role="group" aria-label="Time period selection">
              <button
                className={`engagement-metrics__period-button ${period === '7d' ? 'engagement-metrics__period-button--active' : ''}`}
                onClick={() => handlePeriodChange('7d')}
                aria-pressed={period === '7d'}
              >
                7 days
              </button>
              <button
                className={`engagement-metrics__period-button ${period === '30d' ? 'engagement-metrics__period-button--active' : ''}`}
                onClick={() => handlePeriodChange('30d')}
                aria-pressed={period === '30d'}
              >
                30 days
              </button>
              <button
                className={`engagement-metrics__period-button ${period === '90d' ? 'engagement-metrics__period-button--active' : ''}`}
                onClick={() => handlePeriodChange('90d')}
                aria-pressed={period === '90d'}
              >
                90 days
              </button>
            </div>
          </div>

          {/* Tier Filter */}
          <div className="engagement-metrics__filter-group">
            <label className="engagement-metrics__filter-label" htmlFor="tier-filter">
              Subscription Tier
            </label>
            <select
              id="tier-filter"
              className="engagement-metrics__tier-select"
              value={tier}
              onChange={handleTierChange}
            >
              <option value="all">All Users</option>
              <option value="free">Free</option>
              <option value="premium">Premium</option>
              <option value="family">Family</option>
            </select>
          </div>
        </div>
      </div>

      {/* Metrics Grid */}
      <div className="engagement-metrics__grid">
        {/* Average Session Duration */}
        <div className="engagement-metrics__card">
          <div className="engagement-metrics__card-icon" aria-hidden="true">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"></circle>
              <polyline points="12 6 12 12 16 14"></polyline>
            </svg>
          </div>
          <div className="engagement-metrics__card-content">
            <div className="engagement-metrics__card-label">Avg Session Duration</div>
            <div className="engagement-metrics__card-value">
              {formatDuration(metrics.avgSessionDuration)}
            </div>
            <div className="engagement-metrics__card-subtitle">
              Per session
            </div>
          </div>
        </div>

        {/* Average Sessions Per User */}
        <div className="engagement-metrics__card">
          <div className="engagement-metrics__card-icon" aria-hidden="true">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
              <circle cx="9" cy="7" r="4"></circle>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
            </svg>
          </div>
          <div className="engagement-metrics__card-content">
            <div className="engagement-metrics__card-label">Avg Sessions Per User</div>
            <div className="engagement-metrics__card-value">
              {metrics.avgSessionsPerUser?.toFixed(1) || '0.0'}
            </div>
            <div className="engagement-metrics__card-subtitle">
              Sessions/user
            </div>
          </div>
        </div>

        {/* Total Time in App */}
        <div className="engagement-metrics__card">
          <div className="engagement-metrics__card-icon" aria-hidden="true">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
              <line x1="16" y1="2" x2="16" y2="6"></line>
              <line x1="8" y1="2" x2="8" y2="6"></line>
              <line x1="3" y1="10" x2="21" y2="10"></line>
            </svg>
          </div>
          <div className="engagement-metrics__card-content">
            <div className="engagement-metrics__card-label">Total Time in App</div>
            <div className="engagement-metrics__card-value">
              {formatHours(metrics.totalTimeInApp)}
            </div>
            <div className="engagement-metrics__card-subtitle">
              Total across all users
            </div>
          </div>
        </div>

        {/* Average Time Per User */}
        <div className="engagement-metrics__card">
          <div className="engagement-metrics__card-icon" aria-hidden="true">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
              <circle cx="12" cy="7" r="4"></circle>
            </svg>
          </div>
          <div className="engagement-metrics__card-content">
            <div className="engagement-metrics__card-label">Avg Time Per User</div>
            <div className="engagement-metrics__card-value">
              {formatHours(metrics.avgTimePerUser)}
            </div>
            <div className="engagement-metrics__card-subtitle">
              Average per user
            </div>
          </div>
        </div>
      </div>

      {/* Loading Overlay (when refreshing with existing data) */}
      {loading && data && (
        <div className="engagement-metrics__loading-overlay">
          <div className="engagement-metrics__spinner"></div>
        </div>
      )}
    </div>
  );
};

export default EngagementMetrics;
